import { useMemo, useState } from "react";
import { Check, ChevronDown, Copy, Plus, X } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";

type WidgetKind = "live-agent" | "studio";

const SIZE_PRESETS = [
  { label: "Popup (380×580)", width: "380", height: "580" },
  { label: "Wide popup (900×640)", width: "900", height: "640" },
  { label: "Full page", width: "100%", height: "100%" },
];

const LABEL_CLASS = "text-sm font-semibold text-gray-700 mb-2 block";
const FIELD_CLASS =
  "px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base";

interface Slot {
  id: string;
  kind: WidgetKind;
  agentUrlName: string;
  embed: boolean;
  width: string;
  height: string;
}

const makeSlot = (overrides: Partial<Slot> = {}): Slot => ({
  id: crypto.randomUUID(),
  kind: "live-agent",
  agentUrlName: "lemonade",
  embed: true,
  width: "380",
  height: "580",
  ...overrides,
});

const buildIframeSrc = (slot: Slot) => {
  const base = `${window.location.origin}/${slot.kind}/${encodeURIComponent(slot.agentUrlName.trim())}`;
  return slot.embed ? `${base}?embed=true` : base;
};

const buildEmbedCode = (slot: Slot) => {
  const src = buildIframeSrc(slot);
  return `<iframe
  src="${src}"
  width="${slot.width}"
  height="${slot.height}"
  frameborder="0"
  allow="microphone"
  style="border: none; display: block;"
></iframe>`;
};

const WidgetPreviewSlot = ({
  slot,
  onChange,
  onRemove,
  removable,
}: {
  slot: Slot;
  onChange: (next: Slot) => void;
  onRemove: () => void;
  removable: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const [embedCodeOpen, setEmbedCodeOpen] = useState(false);

  const iframeSrc = useMemo(() => buildIframeSrc(slot), [slot]);
  const embedCode = useMemo(() => buildEmbedCode(slot), [slot]);
  const isFullPage = slot.width.trim().endsWith("%");

  return (
    <div className="bg-white border border-genie-border rounded-xl p-5 md:p-6 flex-1 min-w-[340px]">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={LABEL_CLASS}>Widget</label>
            <select
              value={slot.kind}
              onChange={(e) => onChange({ ...slot, kind: e.target.value as WidgetKind })}
              className={FIELD_CLASS}
            >
              <option value="live-agent">live-agent</option>
              <option value="studio">studio</option>
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>Agent name</label>
            <input
              value={slot.agentUrlName}
              onChange={(e) => onChange({ ...slot, agentUrlName: e.target.value })}
              placeholder="lemonade"
              className={`${FIELD_CLASS} w-44`}
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Mode</label>
            <select
              value={slot.embed ? "embed" : "full"}
              onChange={(e) => onChange({ ...slot, embed: e.target.value === "embed" })}
              className={FIELD_CLASS}
            >
              <option value="embed">Embed</option>
              <option value="full">Full page</option>
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS}>Width</label>
            <input
              value={slot.width}
              onChange={(e) => onChange({ ...slot, width: e.target.value })}
              placeholder="380 or 100%"
              className={`${FIELD_CLASS} w-32`}
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Height</label>
            <input
              value={slot.height}
              onChange={(e) => onChange({ ...slot, height: e.target.value })}
              placeholder="580 or 100%"
              className={`${FIELD_CLASS} w-32`}
            />
          </div>
        </div>

        {removable && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500 hover:bg-red-50 hover:text-red-600 shrink-0"
            aria-label="Remove widget"
            title="Remove widget"
            onClick={onRemove}
          >
            <X />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {SIZE_PRESETS.map((preset) => (
          <Button
            key={preset.label}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({ ...slot, width: preset.width, height: preset.height })}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="border border-genie-border rounded-lg p-3 mb-4">
        <button
          type="button"
          onClick={() => setEmbedCodeOpen((open) => !open)}
          className="flex w-full items-center justify-between"
          aria-expanded={embedCodeOpen}
        >
          <span className="text-xs font-medium text-gray-600">Embed code</span>
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${embedCodeOpen ? "rotate-180" : ""}`}
          />
        </button>
        {embedCodeOpen && (
          <div className="mt-2">
            <div className="flex items-center justify-end mb-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:bg-genie-primary/10 hover:text-genie-primary"
                aria-label="Copy embed code"
                title={copied ? "Copied" : "Copy embed code"}
                onClick={async () => {
                  await navigator.clipboard.writeText(embedCode);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? <Check className="text-genie-primary" /> : <Copy />}
              </Button>
            </div>
            <pre className="text-xs bg-gray-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">{embedCode}</pre>
          </div>
        )}
      </div>

      <div
        className="bg-gray-50 border border-genie-border rounded-xl overflow-auto"
        style={{
          width: isFullPage ? "100%" : undefined,
          height: isFullPage ? "80vh" : undefined,
          padding: "24px",
        }}
      >
        <iframe
          key={iframeSrc + slot.width + slot.height}
          src={iframeSrc}
          width={slot.width}
          height={slot.height}
          frameBorder="0"
          allow="microphone"
          style={{ border: "none", display: "block", maxWidth: "100%" }}
          title="Widget preview"
        />
      </div>
    </div>
  );
};

const WidgetTesterDev = () => {
  const [slots, setSlots] = useState<Slot[]>([makeSlot()]);

  const updateSlot = (id: string, next: Slot) => {
    setSlots((prev) => prev.map((s) => (s.id === id ? next : s)));
  };

  const removeSlot = (id: string) => {
    setSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const addSlot = () => {
    setSlots((prev) => [...prev, makeSlot({ ...prev[prev.length - 1], id: crypto.randomUUID() })]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <PageHeader
          title="Widget Tester"
          subtitle="Preview live-agent / studio widgets as an iframe without file:// origin issues"
          action={
            <button
              onClick={addSlot}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              Add widget to compare
            </button>
          }
        />

        <div className="flex flex-wrap items-start gap-6">
          {slots.map((slot) => (
            <WidgetPreviewSlot
              key={slot.id}
              slot={slot}
              onChange={(next) => updateSlot(slot.id, next)}
              onRemove={() => removeSlot(slot.id)}
              removable={slots.length > 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WidgetTesterDev;
